import { useSmartCardContext } from '@atlaskit/link-provider/use-smart-card-context';
import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';

import {
	ConfluencePageAri,
	JiraIssueAri,
	TownsquareGoalAri,
	TownsquareProjectAri,
	IdentityTeamAri,
	AnyAri,
	CmdbObjectAri,
} from '@atlassian/ari';
import {
	PARENT_PRODUCTS,
	type PanelDataMap,
} from '@atlassian/confluence-page-preview-panel/constants';
import { useConfluencePagePreviewPanel } from '@atlassian/confluence-page-preview-panel/ui';
import { ALL_SUPPORTED_OBJECTS as ALL_PLATFORM_SUPPORTED_OBJECTS } from '@atlassian/preview-panel-global-objects/constants';
import { useGlobalObjectPreviewPanels } from '@atlassian/preview-panel-global-objects/global-object-preview-panels';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { isInIframe } from './utils/previewPanelUtils.ts';

export interface OpenParams {
	url: string;
	ari: string;
	panelData?: PanelDataMap;
}

/**
 * Resolves Confluence short links to canonical URLs for preview panels.
 * For Confluence short links (e.g., /wiki/x/ABC123), attempts to construct
 * the canonical URL from preview.href parameters.
 */
const resolveConfluencePreviewPanelUrl = (
	originalUrl: string,
	response: JsonLd.Response,
): string => {
	const responseData = response?.data as JsonLd.Data.BaseData & { preview?: { href?: string } };

	const previewHref = responseData?.preview?.href;
	if (!previewHref) {
		return originalUrl;
	}

	try {
		const previewUrl = new URL(previewHref);
		const contentId = previewUrl.searchParams.get('contentId');
		const spaceKey = previewUrl.searchParams.get('spaceKey');
		const hostname = previewUrl.searchParams.get('hostname');

		if (contentId && spaceKey && hostname) {
			const decodedSpaceKey = decodeURIComponent(spaceKey);
			return `https://${hostname}/wiki/spaces/${decodedSpaceKey}/pages/${contentId}`;
		}
	} catch {
		// If URL parsing fails, fall back to original URL
		return originalUrl;
	}

	return originalUrl;
};

// Helper function to check if previewPanels query parameter is present
const hasPreviewPanelsParam = (): boolean => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.has('previewPanels');
	} catch {
		return false;
	}
};

const noop = () => {
	// this is intentional
};

export const useOpenPreviewPanel = (): {
	open: ({ url, ari, panelData }: OpenParams) => void;
} => {
	const { cloudId, orgId } = useSessionData();
	const supportedObjects = ALL_PLATFORM_SUPPORTED_OBJECTS;
	const { openPreviewPanel: openAtlasPreviewPanel } = useGlobalObjectPreviewPanels(
		PARENT_PRODUCTS.CONFLUENCE,
		cloudId,
		{ supportedObjects },
	);
	const { openConfluencePagePreviewPanel } = useConfluencePagePreviewPanel(
		PARENT_PRODUCTS.CONFLUENCE,
		cloudId,
	);
	const cardContext = useSmartCardContext();

	const open = ({ url, ari, panelData }: OpenParams): void => {
		let resolvedUrl = url;

		// Only resolve when url is Confluence short links
		if (url.includes('/wiki/x/') && cardContext?.value?.store) {
			const cardState = cardContext.value.store.getState()[url];
			if (cardState?.details) {
				resolvedUrl = resolveConfluencePreviewPanelUrl(url, cardState.details);
			}
		}

		const name = AnyAri.parse(ari).resourceType;
		const formattedName = name.charAt(0).toUpperCase() + name.slice(1);

		if (isInIframe() && hasPreviewPanelsParam()) {
			// Post message to parent frame to open the panel
			window.parent.postMessage(
				{
					type: 'OPEN_PREVIEW_PANEL',
					payload: {
						url: resolvedUrl,
						ari,
						name: formattedName,
						panelData,
					},
				},
				'*',
			);
			return;
		}

		if (ConfluencePageAri.check(ari)) {
			const urlObj = new URL(resolvedUrl);
			urlObj.searchParams.set('previewPanels', encodeURIComponent(supportedObjects.join(',')));
			openConfluencePagePreviewPanel(urlObj.toString(), noop, panelData);
			return;
		}

		if (TownsquareGoalAri.check(ari)) {
			openAtlasPreviewPanel({ url, ari, name: 'Goal', parentProduct: PARENT_PRODUCTS.CONFLUENCE });
			return;
		}

		if (TownsquareProjectAri.check(ari)) {
			openAtlasPreviewPanel({
				url,
				ari,
				name: 'Project',
				parentProduct: PARENT_PRODUCTS.CONFLUENCE,
			});
			return;
		}

		if (JiraIssueAri.check(ari)) {
			openAtlasPreviewPanel({
				url,
				ari,
				name: 'Jira Work Item',
				parentProduct: PARENT_PRODUCTS.CONFLUENCE,
			});
			return;
		}

		if (IdentityTeamAri.check(ari)) {
			openAtlasPreviewPanel({ url, ari, name: 'Team', parentProduct: PARENT_PRODUCTS.CONFLUENCE });
			return;
		}

		if (CmdbObjectAri.check(ari) && fg('open_preview_panel_for_assets_object_in_confluence')) {
			openAtlasPreviewPanel({
				url,
				ari,
				name: 'Object',
				organizationId: orgId,
				parentProduct: PARENT_PRODUCTS.CONFLUENCE,
			} as Parameters<typeof openAtlasPreviewPanel>[0] & { organizationId?: string });
			return;
		}
	};

	return { open };
};
